import { useEffect, useMemo } from 'react'

import { GenericalRecord } from '@eastcoal/common/types/utilities'
import { pick } from '@eastcoal/common/utils/object'

import { FormItem } from '@eastcoal/formily/components/FormItem'
import { SchemaForm } from '@eastcoal/formily/components/SchemaForm'
import { SchemaFormProps } from '@eastcoal/formily/components/SchemaForm/interface'
import { onFormInputChange } from '@eastcoal/formily/exports/core'
import { useCreationForm } from '@eastcoal/formily/hooks/useCreationForm'

import { $getNodeByKey } from '@eastcoal/editor/exports'
import { useEditor } from '@eastcoal/editor/hooks/useEditor'

import { FontColorPicker } from '../../../../@formily/FontColorPicker'
import { FontFamilySelect } from '../../../../@formily/FontFamilySelect'
import { FontSizeSelect } from '../../../../@formily/FontSizeSelect'
import { $isTextVariableNode } from '../../../../editor/nodes/TextVariableNode'
import { TextVariableNodeState } from '../../../../editor/nodes/TextVariableNode/interface'
import { useContractDesignerStore } from '../../../Store/hooks/useStore'
import { FontStyleCheckboxGroup } from '../components/FontStyleCheckboxGroup'

const schema: SchemaFormProps<GenericalRecord>['schema'] = [
  {
    name: 'fontFamily',
    title: '字体',
    type: 'string',
    'x-component': FontFamilySelect,
    'x-decorator': FormItem,
    'x-decorator-props': {
      wrapperWidth: 160
    }
  },
  {
    name: 'fontSize',
    title: '字号',
    type: 'number',
    'x-component': FontSizeSelect,
    'x-decorator': FormItem,
    'x-decorator-props': {
      wrapperWidth: 160
    }
  },
  {
    name: '{fontWeight, textDecoration}',
    title: '文本样式',
    type: 'object',
    'x-component': FontStyleCheckboxGroup,
    'x-decorator': FormItem
  },
  {
    name: 'color',
    title: '文本颜色',
    type: 'string',
    'x-component': FontColorPicker,
    'x-component-props': {
      showText: true,
      placement: 'left'
    },
    'x-decorator': FormItem
  }
]

const grid: SchemaFormProps<GenericalRecord>['grid'] = {
  columns: 1
}

export const TextVariableConfigure = () => {
  const { activedVariableNodeKey } = useContractDesignerStore(state =>
    pick(state, ['activedVariableNodeKey'])
  )
  const editor = useEditor()
  const textVariableNode = useMemo(() => {
    if (!activedVariableNodeKey) return undefined
    return editor.getEditorState().read(() => {
      const node = $getNodeByKey(activedVariableNodeKey)
      if ($isTextVariableNode(node)) {
        return node
      }
      return undefined
    })
  }, [activedVariableNodeKey, editor])

  const form = useCreationForm({
    effects(form) {
      // 监听表单值变化，更新表格变量节点的样式配置
      onFormInputChange(form => {
        editor.update(() => {
          const { fontFamily, fontSize, color, fontWeight, textDecoration } =
            form.values
          const styleConfig: TextVariableNodeState['styleConfig'] = {}
          if (fontFamily) {
            styleConfig.fontFamily = fontFamily
          }
          if (fontSize) {
            styleConfig.fontSize = `${fontSize}px`
          }
          if (color) {
            styleConfig.color = color
          }
          if (fontWeight) {
            styleConfig.fontWeight = fontWeight
          }
          if (textDecoration) {
            styleConfig.textDecoration = textDecoration
          }
          textVariableNode?.setStyleConfig(styleConfig)
        })
      })
    }
  })

  // 初始化表格变量节点的样式配置到表单
  useEffect(() => {
    if (!textVariableNode) return
    const styleConfig = textVariableNode.getStyleConfig()
    form.setInitialValues({
      ...styleConfig,
      fontWeight: styleConfig.fontWeight ?? '',
      textDecoration: styleConfig.textDecoration ?? '',
      fontSize: styleConfig.fontSize
        ? Number.parseFloat(`${styleConfig.fontSize}`)
        : undefined
    })
  }, [activedVariableNodeKey, editor, form, textVariableNode])

  return (
    <SchemaForm
      form={form}
      grid={grid}
      name='textVariableConfigureForm'
      schema={schema}
    />
  )
}
